/**
 * Contexto de medición de un DWG/DXF que antes vivía dentro de `PlanosPage`: la escala vigente, qué falta por
 * resolverla y la altura de cada planta. Funciones PURAS para que la pantalla del plano y «Cambió el plano» (que mide
 * el plano de la hoja y la revisión nueva) usen exactamente la misma regla. El cuerpo es el que tenía la pantalla.
 */
import { factorDeEscala, type PlanoEscalaElegida, type PlanoInventario } from '@supernova/shared';
import type { PlanoAlturaPropuesta } from '../api/planos.js';
import { rotuloDeVista } from './medirElemento.js';

/** La unidad que la persona fijó en la pantalla del plano; 'archivo' = la que dice el archivo (o la elegida). */
export type UnidadPlanoSel = 'archivo' | 'm' | 'cm' | 'mm';

/**
 * Factor de escala vigente (unidad del dibujo → metro): la unidad fijada, con el $INSUNITS como default.
 * «Según el archivo»: la política vive en @supernova/shared; prefiere la escala REAL derivada de las cotas cuando es
 * decisiva y, si no, cae al $INSUNITS del header.
 */
export function factorEscalaDwg(
  inventario: PlanoInventario | null | undefined,
  unidadPlanoSel: UnidadPlanoSel,
  escalaElegida: PlanoEscalaElegida | null | undefined,
): number | null {
  if (unidadPlanoSel === 'm') return 1;
  if (unidadPlanoSel === 'cm') return 0.01;
  if (unidadPlanoSel === 'mm') return 0.001;
  return factorDeEscala(inventario, escalaElegida).factor;
}

/** Escala dudosa sin respuesta: no hay medidas que mostrar hasta que la persona diga la unidad. */
export function escalaSinResolverDwg(
  inventario: PlanoInventario | null | undefined,
  unidadPlanoSel: UnidadPlanoSel,
  escalaElegida: PlanoEscalaElegida | null | undefined,
): boolean {
  return unidadPlanoSel === 'archivo' && factorDeEscala(inventario, escalaElegida).dudosa;
}

/**
 * DWG con varias plantas: la altura de cada planta y de dónde sale —de las cotas de la fachada, o elegida por la IA
 * entre las que trae la fachada (por el rótulo), o sin dato—. Nunca un valor típico.
 */
export function alturaPorVistaDwg(
  inventario: PlanoInventario | null | undefined,
  alturaProp: PlanoAlturaPropuesta | null | undefined,
): Map<string, { altoM: number | null; origen: string }> {
  const out = new Map<string, { altoM: number | null; origen: string }>();
  const vs = inventario?.vistas ?? [];
  for (const v of vs) {
    if (!v.suma) continue;
    const al = v.altura;
    const fuente = al?.vista_fuente ? vs.find((x) => x.id === al.vista_fuente) : undefined;
    const nombreFuente = fuente ? `«${rotuloDeVista(fuente, vs.indexOf(fuente))}»` : 'la fachada';
    if (al?.origen === 'cota_de_elevacion' && al.altura_m != null) {
      out.set(v.id, { altoM: al.altura_m, origen: `de las cotas de ${nombreFuente}` });
    } else if (al?.origen === 'por_desempatar') {
      const ia = alturaProp?.porVista?.[v.id];
      out.set(
        v.id,
        ia
          ? { altoM: ia.alturaM, origen: `de ${nombreFuente}: trae ${al.candidatas_m.join(' y ')} m y la IA eligió esta por el rótulo` }
          : { altoM: null, origen: `${nombreFuente} trae ${al.candidatas_m.join(' y ')} m y no dice cuál es de esta planta: escríbela` },
      );
    } else {
      out.set(v.id, { altoM: null, origen: 'el plano no trae una fachada o un corte con esta altura: escríbela' });
    }
  }
  return out;
}
